import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import MaskedLines from "../components/MaskedLines";
import { useAuth } from "../lib/auth";

export default function AuthPage({ mode }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/portal";

  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return toast.error("Enter a valid email");
    if (f.password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f.name, f.email, f.password);
      toast.success(mode === "login" ? "Welcome back." : "Welcome to the Investor Desk.");
      nav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-[112px]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 min-h-[70vh]">
        <div className="md:col-span-6 flex flex-col justify-center">
          <div className="eyebrow mb-6">Investor Desk · {mode === "login" ? "Sign In" : "Register"}</div>
          <h1 className="font-serif text-[52px] md:text-[80px] leading-[0.95] tracking-tight">
            <MaskedLines lines={mode === "login" ? ["Welcome back", "to the desk."] : ["Request a seat", "at the desk."]} />
          </h1>
          <p className="mt-8 max-w-md text-[14px] leading-[1.8] text-[#EDE6D3]/70">
            {mode === "login"
              ? "Sign in to view your live opportunity pipeline and submit access requests."
              : "Registration is the first step. A partner reviews every account before pipeline access is granted."}
          </p>
        </div>

        <form onSubmit={submit} className="md:col-span-6 card-sand p-10 md:p-16 self-center w-full max-w-xl mx-auto" data-testid={`auth-form-${mode}`}>
          <div className="space-y-8">
            {mode !== "login" && (
              <div><div className="bb-label">Full Name</div><input required className="bb-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="auth-name" /></div>
            )}
            <div><div className="bb-label">Email</div><input required type="email" className="bb-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="auth-email" /></div>
            <div><div className="bb-label">Password</div><input required type="password" className="bb-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="auth-password" /></div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-10 disabled:opacity-50" data-testid={`auth-submit-${mode}`}>
            {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"} <ArrowRight size={14} />
          </button>
          <div className="mt-8 text-[12px] text-[#EDE6D3]/60 flex items-center justify-between">
            {mode === "login" ? (
              <>New here? <Link to={`/register?next=${next}`} className="link-underline text-[#EDE6D3]">Create an account →</Link></>
            ) : (
              <>Already registered? <Link to={`/login?next=${next}`} className="link-underline text-[#EDE6D3]">Sign in →</Link></>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
